import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge } from '@xiltrion/ui/badge';
import { EstadoCargando } from '@xiltrion/ui/bloques/estados';
import { LogoDatalog } from '@xiltrion/ui/marca/logo-datalog';
import { cn } from '@xiltrion/ui/utils';
import { apiGet } from '@/api/client';
import type { PublicApp } from '@/api/types';
import { LogoApp } from '@/app/LogoApp';

/**
 * La portada de Datalog, en negro (`.portada-suite` de `index.css`, que también vuelve negros los botones y enlaces del
 * contenido): a la izquierda los logos de las apps, a la derecha `children`. Es el ingreso sin entidad (`PortadaSuite`)
 * y el marco de todo lo demás que la suite muestra fuera del portal (elegir entidad en una interacción OIDC, activar la
 * cuenta, restablecer la contraseña): la suite no tiene otra pantalla de acceso (GUIA §3.1).
 *
 * `apps` sin dar: el catálogo público. `elegir`: los logos son botones (la portada sin entidad); si no, solo se muestran.
 */
export function MarcoSuite({ apps, app, elegir, superadmin, children }: { apps?: PublicApp[]; app?: PublicApp | null; elegir?: (code: string | null) => void; superadmin?: boolean; children: ReactNode }) {
  const catalogo = useQuery({ queryKey: ['public-apps'], queryFn: () => apiGet<PublicApp[]>('/public/apps'), enabled: !apps, staleTime: 5 * 60_000, retry: false });
  const lista = apps ?? catalogo.data ?? [];
  return (
    <main className="portada-suite grid min-h-dvh lg:grid-cols-[1.1fr_1fr]">
      <section className="relative flex flex-col justify-between gap-8 bg-(--portada-negro) p-8 text-(--portada-negro-foreground) lg:p-12">
        <div aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.07]" style={{ backgroundImage: 'linear-gradient(currentColor 1px, transparent 1px), linear-gradient(90deg, currentColor 1px, transparent 1px)', backgroundSize: '44px 44px', maskImage: 'linear-gradient(to bottom, black, transparent 75%)' }} />
        <div className="relative flex items-center gap-3">
          <LogoDatalog />
          {superadmin && <Badge variant="outline" className="border-(--portada-negro-foreground)/40 text-(--portada-negro-foreground)">Super Admin</Badge>}
        </div>
        <div className="relative space-y-8">
          <div>
            <h2 className="text-[2.3rem] font-semibold leading-[1.06] tracking-[-0.025em] xl:text-[2.7rem]">Todo lo que mueve tu entidad,<br /><span className="opacity-70">empieza aquí</span></h2>
            <p className="mt-4 max-w-xl text-[15px] leading-relaxed opacity-80">Planeación, gestión documental y finanzas conectadas con una sola cuenta.</p>
          </div>
          <ul className="grid max-w-xl grid-cols-3 gap-3">
            {lista.map((a, i) => {
              const contenido = (
                <>
                  <LogoApp app={a} className={cn('w-20 transition-transform duration-500 lg:w-24', elegir && 'group-hover:-translate-y-1', app?.code === a.code && 'scale-110')} />
                  <span className="text-sm font-semibold">{a.name}</span>
                  <span className="text-xs leading-snug opacity-80">{a.description}</span>
                </>
              );
              const clase = cn('group flex w-full flex-col items-center gap-2.5 rounded-2xl p-3 text-center outline-none transition-opacity focus-visible:ring-2 focus-visible:ring-(--portada-negro-foreground)/60', app && app.code !== a.code && 'opacity-60 hover:opacity-100');
              return (
                <li key={a.code} className="animate-in fade-in slide-in-from-bottom-2 duration-500 fill-mode-both" style={{ animationDelay: `${150 + i * 80}ms` }}>
                  {elegir ? <button type="button" aria-pressed={app?.code === a.code} onClick={() => elegir(app?.code === a.code ? null : a.code)} className={clase}>{contenido}</button> : <div className={clase}>{contenido}</div>}
                </li>
              );
            })}
          </ul>
        </div>
        <p className="relative text-xs opacity-60">© {new Date().getFullYear()} Datalog</p>
      </section>
      <section className="flex items-center justify-center bg-background p-6 lg:p-12">
        <div className="w-full max-w-sm">{children}</div>
      </section>
    </main>
  );
}

/** Título y subtítulo del lado derecho del marco, como los del formulario de ingreso. */
export function TituloMarco({ app, titulo, subtitulo }: { app?: { code: string; icon: string; color?: number | null } | null; titulo: string; subtitulo?: ReactNode }) {
  return (
    <div className="mb-6">
      {app && <LogoApp app={app} className="mb-5 w-16" />}
      <h1 className="text-2xl font-semibold tracking-tight">{titulo}</h1>
      {subtitulo && <p className="mt-1 text-sm text-muted-foreground">{subtitulo}</p>}
    </div>
  );
}

/** Mientras algo aún no se sabe (una interacción que va a terminar sola): solo el indicador, sin marco de acceso. */
export function EsperaAcceso() {
  return <div className="grid min-h-dvh place-items-center bg-background"><EstadoCargando /></div>;
}
